import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    Percent,
    Wallet,
    KeyRound,
    Sparkles,
    Link2Off,
    Palmtree,
    Gem,
    Layers,
    Landmark,
    HandCoins,
    ArrowRight,
} from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const TYPES = [
    {
        icon: Percent,
        title: "Hipoteca 100%",
        desc: "Financiación total, sin pago inicial. La llave a tu vivienda aunque no tengas ahorros previos.",
        slug: "hipoteca-100",
        formType: "Hipoteca 100%",
    },
    {
        icon: Wallet,
        title: "Hipoteca 95%",
        desc: "Reduce tu desembolso con una opción flexible.",
        slug: "hipoteca-95",
        formType: "Hipoteca 95%",
    },
    {
        icon: KeyRound,
        title: "Hipoteca 90%",
        desc: "Ideal para acceder a tu vivienda con esfuerzo reducido.",
        slug: "hipoteca-90",
        formType: "Hipoteca 90%",
    },
    {
        icon: Sparkles,
        title: "Hipoteca joven",
        desc: "Diseñada para menores de 35: 90%, 95% y 100%.",
        slug: "hipoteca-joven",
        formType: "Hipoteca joven",
    },
    {
        icon: Gem,
        title: "Hipoteca Premium",
        desc: "Financiación para propiedades de alto valor.",
        slug: "hipoteca-premium",
        formType: "Hipoteca Premium",
    },
    {
        icon: Link2Off,
        title: "Sin vinculaciones",
        desc: "Sin seguros ni productos asociados obligatorios.",
        slug: "hipoteca-sin-vinculaciones",
        formType: "Sin vinculaciones",
    },
    {
        icon: Palmtree,
        title: "80% Segunda Viv/Inv",
        desc: "Compra fácil para tu casa de vacaciones o como inversión.",
        slug: "hipoteca-segunda-vivienda",
        formType: "Segunda vivienda",
    },
    {
        icon: Landmark,
        title: "Capital privado",
        desc: "Solución para financiaciones no aceptadas por bancos.",
        slug: "capital-privado",
        formType: "Capital privado",
    },
    {
        icon: Layers,
        title: "Reunificación de deudas",
        desc: "Agrupa tus deudas en una sola cuota.",
        slug: "reunificacion-deudas",
        formType: "Reunificación de deudas",
    },
    {
        icon: HandCoins,
        title: "¿Necesitas liquidez?",
        desc: "Amplía tu hipoteca y obtén dinero extra para lo que necesites.",
        slug: "necesitas-liquidez",
        formType: "Necesito liquidez",
        path: "/necesitas-liquidez",
    },
];

const reveal = (i) => ({
    initial: { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.55, delay: (i % 3) * 0.08 },
});

export default function MortgageTypes() {
    const wantIt = (formType) => {
        window.dispatchEvent(
            new CustomEvent("kp:prefill", { detail: { mortgage_type: formType } })
        );
        scrollToId("contacto");
    };

    return (
        <section
            id="hipotecas"
            data-testid="mortgage-types-section"
            className="relative overflow-hidden bg-[#112338] noise-overlay py-20 lg:py-28 px-4 sm:px-6 lg:px-8"
        >
            <div className="absolute inset-0 blueprint-grid" />
            <div
                className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle at center, rgba(16,185,129,0.1) 0%, transparent 70%)",
                }}
            />
            <div className="relative max-w-7xl mx-auto">
                <motion.div {...reveal(0)} className="max-w-2xl">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-emerald-400">
                        Tipos de hipoteca
                    </p>
                    <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
                        Encuentra la hipoteca que mejor se adapta a ti.
                    </h2>
                </motion.div>

                <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {TYPES.map((t, i) => (
                        <motion.div
                            key={t.title}
                            data-testid={`mortgage-card-${t.slug}`}
                            {...reveal(i)}
                            className="h-full bg-white rounded-3xl border border-white/10 p-7 transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-black/40 hover:border-emerald-400/50 flex flex-col"
                        >
                            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#1F3B5B]/5 text-[#1F3B5B]">
                                <t.icon size={20} />
                            </span>
                            <h3 className="mt-5 font-display font-semibold text-lg text-[#0F172A]">
                                {t.title}
                            </h3>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">
                                {t.desc}
                            </p>
                            <div className="mt-5 flex items-center justify-between gap-3">
                                <button
                                    data-testid={`mortgage-want-${t.slug}`}
                                    onClick={() => wantIt(t.formType)}
                                    className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0B1A2B] text-sm font-bold px-4 py-2 transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
                                >
                                    La quiero
                                </button>
                                <Link
                                    data-testid={`mortgage-readmore-${t.slug}`}
                                    to={t.path || `/hipotecas/${t.slug}`}
                                    className="group/link inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 hover:text-emerald-700 transition-colors"
                                >
                                    Leer más
                                    <ArrowRight
                                        size={15}
                                        className="transition-transform duration-200 group-hover/link:translate-x-1"
                                    />
                                </Link>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.div {...reveal(2)} className="mt-12 text-center">
                    <button
                        data-testid="mortgage-types-cta"
                        onClick={() => scrollToId("calculadora")}
                        className="group inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0B1A2B] font-bold px-8 py-4 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 shadow-[0_10px_40px_rgba(16,185,129,0.3)]"
                    >
                        Simula tu hipoteca ahora
                        <ArrowRight
                            size={17}
                            className="transition-transform duration-200 group-hover:translate-x-1"
                        />
                    </button>
                </motion.div>
            </div>
        </section>
    );
}
